import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../../utils/constants';
import '../AccountStyles.css';

function LedgersScreen() {
  const navigate = useNavigate();
  const [glAccounts, setGLAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Hardcoded ledger accounts
  const ledgerAccounts = [
    'Advance Deposit Ledger',
    'Guest Ledger',
    'Group Ledger',
    'Travel Agent Ledger',
    'Owner Ledger',
    'Work Order Ledger',
    'tax1',
    'tax2',
    'tax3',
    'tax4',
    'tax5',
    'tax6'
  ];

  // State for selected GL accounts for each ledger
  const [ledgerMappings, setLedgerMappings] = useState({});
  const [existingMappings, setExistingMappings] = useState([]);

  // Check authentication on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
  }, [navigate]);

  useEffect(() => {
    fetchGLAccounts();
    fetchLedgerMappings();
  }, []);

  const fetchGLAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_URL}/gl-accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setGLAccounts(data);
      } else {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        const errorData = await response.json();
        setError(errorData.message || 'Error fetching GL accounts');
      }
    } catch (error) {
      console.error('Error fetching GL accounts:', error);
      setError('Error fetching GL accounts. Please try again.');
    }
  };

  const fetchLedgerMappings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_URL}/ledger-mappings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setExistingMappings(data);
        
        // Initialize ledger mappings with existing values or empty strings
        const initialMappings = {};
        ledgerAccounts.forEach(ledger => {
          const existingMapping = data.find(mapping => mapping.ledger_name === ledger);
          initialMappings[ledger] = existingMapping ? existingMapping.gl_account_id.toString() : '';
        });
        setLedgerMappings(initialMappings);
        setLoading(false);
      } else {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        const errorData = await response.json();
        setError(errorData.message || 'Error fetching ledger mappings');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching ledger mappings:', error);
      setError('Error fetching ledger mappings. Please try again.');
      setLoading(false);
    }
  };

  const handleLedgerChange = (ledgerName, glAccountId) => {
    setLedgerMappings(prev => ({
      ...prev,
      [ledgerName]: glAccountId
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Convert ledger mappings to the format expected by the API
      const mappingsToSave = Object.entries(ledgerMappings)
        .filter(([ledger, glAccountId]) => glAccountId) // Only save mappings that have a GL account selected
        .map(([ledger, glAccountId]) => ({
          ledger_name: ledger,
          gl_account_id: parseInt(glAccountId)
        }));

      if (mappingsToSave.length === 0) {
        setError('Please select at least one GL account mapping before saving.');
        setSaving(false);
        return;
      }

      const response = await fetch(`${API_URL}/ledger-mappings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ledgerMappings: mappingsToSave }),
      });

      if (response.ok) {
        setSuccess('Ledger mappings saved successfully!');
        // Refresh the mappings to show the updated data
        fetchLedgerMappings();
      } else {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        const errorData = await response.json();
        setError(errorData.message || 'Error saving ledger mappings');
      }
    } catch (error) {
      console.error('Error saving ledger mappings:', error);
      setError('Error saving ledger mappings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getAccountTypeBadge = (type) => {
    const colors = {
      'Asset': 'success',
      'Liability': 'warning',
      'Revenue': 'info',
      'Expense': 'danger'
    };
    return <span className={`badge bg-${colors[type] || 'secondary'}`}>{type}</span>;
  };

  if (loading) {
    return (
      <Container fluid className="account-screen">
        <div className="text-center mt-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="account-screen p-0">
      <Row className="g-0">
        {/* Main Content */}
        <Col xs={12} className="main-content">
          <div style={{ height: 'calc(100vh - 120px)', overflowY: 'auto' }}>
            <Row>
              <Col>
                {/* Header */}
                <Card className="mb-3">
                  <Card.Body>
                    <h1 className="mb-0">Ledger Account Mappings</h1>
                    <p className="text-muted mb-0">
                      Map each ledger account to its corresponding GL account
                    </p>
                  </Card.Body>
                </Card>

                {error && (
                  <Alert variant="danger" onClose={() => setError('')} dismissible className="mb-3">
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert variant="success" onClose={() => setSuccess('')} dismissible className="mb-3">
                    {success}
                  </Alert>
                )}

                {/* Ledger Mappings Form */}
                <Card>
                  <Card.Body>
                    <Form>
                      {ledgerAccounts.map((ledger, index) => (
                        <Row key={ledger} className="mb-3 align-items-center">
                          <Col md={4}>
                            <Form.Label className="fw-bold">
                              {ledger}
                            </Form.Label>
                          </Col>
                          <Col md={8}>
                            <Form.Select
                              value={ledgerMappings[ledger] || ''}
                              onChange={(e) => handleLedgerChange(ledger, e.target.value)}
                              required
                            >
                              <option value="">Select GL Account</option>
                              {glAccounts.map(glAccount => (
                                <option key={glAccount.id} value={glAccount.id}>
                                  {glAccount.account_number} - {glAccount.description} ({glAccount.account_type})
                                </option>
                              ))}
                            </Form.Select>
                          </Col>
                        </Row>
                      ))}

                      {/* Save Button */}
                      <Row className="mt-4">
                        <Col className="text-center">
                          <Button
                            variant="primary"
                            size="lg"
                            onClick={handleSave}
                            disabled={saving}
                          >
                            {saving ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                  className="me-2"
                                />
                                Saving...
                              </>
                            ) : (
                              'Save Ledger Mappings'
                            )}
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>

      <style>
        {`
          .main-content {
            padding: 20px;
          }
          
          .badge {
            font-size: 0.75rem;
            padding: 0.25rem 0.5rem;
          }
        `}
      </style>
    </Container>
  );
}

export default LedgersScreen;
